/**
 * 集团资产经营总览专用接口与口径。只读请求，不切换全局管理方。
 * 复制来源：energy-screen/api/energyScreen.js（权限列表）、
 * energy-screen/api/energyScreenApi.js（用电、设备、光伏）、
 * api/groupBillCollection.js（水电收缴）、api/photovoltaic.js（电站编号、层级拆包、运行统计）。
 * 不依赖或修改这些页面的接口实现。
 * 单项目请求后汇总：集团 = 当前有权限且纳入大屏的项目，禁止平均各项目百分比。
 */
import request from '@/utils/request'
import moment from 'moment'
import { aggregatePortfolio, overviewAlerts, overviewParkingPreview, overviewChargingPreview } from './mock'

const HEADERS = { userDeviceType: 'PC', 'Content-Type': 'application/json' }
const CONTENT = '/api/ebc-content'
export const SOURCE_NOTES = [
  { name: '南都光伏当日运行', type: '接口 / 计算', detail: '复制 station/day 与 api/ems 的 powerParam 请求；光伏 power×powerPec，EMS 每设备五分钟内最大功率后跨设备相加；电网=光伏−负载，负值为购电。缺失保持空白，不用电站自洽负载替代。日期固定为本次查询日，不跟随月年周期。' },
  { name: '预付费水电收缴', type: '接口 / 计算', detail: 'tenantSettleStatis 增加 payType=0，按管理方查询预付费电费与水费；不含后付费、预充值余额、停车、充电。待收=应收−实收，实收率=各项目实收之和÷应收之和，不平均项目比例。' },
  { name: '本期用水 / 租户与公区', type: '接口 / 汇总', detail: '复制 groupEnergyOverview 的 useReport/queryTenantWaterUseStatis，tenantType=0/1 分别查询租户、公区，dataType=2、useType=0；waterUse 按项目求和，单位 m³。空列表为零，失败不填模拟值。' },
  { name: '综合用电 / 项目悬浮卡用电', type: '接口 / 计算', detail: 'getShareEnergy：普通租户、租户空调、普通公区、公区空调四项相加，单位 kWh；项目列表与集团使用同一批结果。' },
  { name: '设备在线率 / 项目标记在线率', type: '接口 / 计算', detail: 'getDeviceTopStatistics：电表、水表、空调内机的总量与离线量；在线 = 总量 − 离线，在线率按数量加权。这不是配电设备专属在线率。' },
  { name: '光伏发电 / 自用 / 自用率 / 出力', type: '接口 / 计算', detail: '仅南都光伏站（1299184320439459357）：station/month 或 year 的 energy、oneSelf、gridSellEnergy 按 energyPec 和 energyStr 换算为 kWh。自用与上网之和通过发电量校验才展示；自用率 = 自用 / 发电。detail 的 power 按 powerStr 换算。光伏请求缓存最多 2 分钟。' },
  { name: '光伏发电占比 / 电网购电', type: '计算', detail: '占比沿用能源大屏公式：同期南都光伏发电量 ÷ 当前所选项目四类用电合计 × 100%；范围不含南都时不外推电站数据。名称明确为发电占比，不等同自用率或购绿电比例。电网购电沿用能源大屏：max(0, 综合用电 − 光伏发电 + 电站购电修正)。这是平衡计算值，非独立购电总表实测。' },
  { name: '账单金额 / 租户应收 / 停车 / 充电', type: '真实 + 预演', detail: '租户应收取 tenantSettleStatis 的 billTotalAmt，与租户账单页口径一致，不传 payType，包含全部付费模式；停车取停车业务接口的真实收入；充电仍为模拟。租户账单按项目汇总；缺失不补零，不用实收替代应收。综合用电含公区，不能直接与租户应收换算。' },
  { name: '停车运营', type: '接口 / 计算', detail: '停车业务接口按车场汇总收入与进出场：总收入及临停、月租续费、储值、内部四类收入相加闭合；总车位为集团车、内部车、月租车、储值车、访客车五类数量合计，月租车位取对应类别数量；总车流量、进场、出场取车场进出场统计；周转率 = 出场车次 ÷ 接入总车位。仅统计纳入集团大屏的项目。' },
  { name: '空调运行', type: '接口', detail: '内机总量与离线数取设备统计（与顶部设备在线率同一口径）；运行/待机取空调设备列表实测并按“总量−离线”闭合；空调用电取租户空调 + 公区空调分项，仅已接入分项计量的项目有值；原“空调用电趋势”已替换为按统计周期的“空调用电构成”，只使用 getShareEnergy 的租户空调/公区空调，不再调用 useReport/queryTenantUseStatis、air/listAirUse 等设备用能报表接口。台均电耗 = 本期空调用电 ÷ 内机总量。原 COP 为模拟值，已移除；楼层空间监测仍为演示；遥控器只读，不调用任何控制接口。' },
  { name: 'CO₂减排', type: '接口 / 累计', detail: '沿用能源大屏：station/detail 的 powerStationAvoidedCo2（kg）÷1000 = t，仅南都电站累计成果，不随月年筛选缩放，不自行估算排放系数。' },
  { name: '本期待收金额 / 待收占比', type: '真实计算', detail: '经营总览收缴模块仅统计预付费：待收=预付费水电应收−实收；待收占比=待收÷应收。不展示账户充值余额。经营总览与能源费用页共用同一份预付费统计。' },
  { name: '光伏收益分析', type: '接口 / 估算', detail: '复制 settleTenantStatistics，南都 managerId=1000107、tenantType=3，取上个自然月的表计电费；预计年收益=该金额×12，收益率=预计年收益÷76万元，回收期=76万元÷预计年收益。建设容量330.86kWp、参考单价0.672来自原能源页配置。上月发电取 station/month。预测不等于实际利润或承诺收益。' },
  { name: '配电房 / 变压器 / 风险 / 巡视 / 工单', type: '模拟', detail: '配电资产、告警和闭环工单保留演示，不能用通用设备离线记录或其他业务工单替代配电风险。' },
  { name: '照明 / 充电 / 配电概览', type: '模拟', detail: '照明策略与节电、充电桩收入/电量/枪位、配电房与变压器运行保留演示，当前无对应接口，界面上统一标注“模拟”。' },
  { name: '其他业务页 / 项目子屏', type: '原有口径', detail: '经营总览及能源与费用共用真实用电、收缴、光伏数据；其他专业页保留模拟口径，在本说明和汇报稿中披露；项目子屏保持已有实现。' }
]

export function numberOrNull (v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
  const value = Number(v)
  return Number.isFinite(value) ? value : null
}
export function formatMetric (v, digits = 0) {
  const value = numberOrNull(v)
  return value === null ? '—' : value.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}
export function divide (a, b) { return numberOrNull(a) === null || numberOrNull(b) === null || b <= 0 ? null : a / b }
const round = v => Number(v.toFixed(2))
function required (data, key) {
  const v = numberOrNull(data && data[key])
  if (v === null) throw new Error('返回数据缺少有效字段：' + key)
  return v
}
function success (response) {
  if (!response || String(response.code) !== '200' || response.data == null) throw new Error('接口未返回有效业务数据')
  return response.data
}
export function overviewRange (period = 'month', now = new Date()) {
  const end = moment(now)
  if (period === 'last') end.subtract(1, 'month').endOf('month')
  const start = end.clone().startOf(period === 'year' ? 'year' : 'month')
  return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD'), label: period === 'year' ? end.format('YYYY年累计') : end.format('YYYY年MM月'), period }
}
export function overviewPeriods (now) { return ['month', 'last', 'year'].map(id => ({ id, label: overviewRange(id, now).label })) }
export function managerDto (id, range) {
  return { managerId: Number(id), firstPermissionMaId: Number(id), startTime: range.startDate + ' 00:00:00', endTime: range.endDate + ' 23:59:59' }
}
export function normalizeEnergy (data) {
  const values = ['tenantValue', 'tenantAirValue', 'publicValue', 'publicAirValue'].map(k => required(data, k))
  return { kwh: values.reduce((a, b) => a + b, 0), tenant: values[0] + values[1], common: values[2] + values[3], tenantGeneral: values[0], tenantAir: values[1], commonGeneral: values[2], commonAir: values[3] }
}
export function normalizeBills (data) {
  const fees = round(required(data, 'elecBillTotalAmt') + required(data, 'waterBillTotalAmt'))
  const received = round(required(data, 'elecPayTotalAmt') + required(data, 'waterPayTotalAmt'))
  return { fees, received, outstanding: round(fees - received), electricityFees: required(data, 'elecBillTotalAmt'), waterFees: required(data, 'waterBillTotalAmt'), electricityReceived: required(data, 'elecPayTotalAmt'), waterReceived: required(data, 'waterPayTotalAmt') }
}
// 复制 groupEnergyOverview 的租户/公区用水查询口径，空列表为接口返回零。
export function normalizeWater (tenant, common) {
  const sum = records => { if (!Array.isArray(records)) throw new Error('用水统计格式异常'); return records.reduce((total, row) => total + required(row, 'waterUse'), 0) }
  const tenantWater = sum(tenant); const commonWater = sum(common)
  return { tenantWater, commonWater, water: round(tenantWater + commonWater) }
}
export function normalizeDevices (data) {
  let devices = 0; let offline = 0
  const categories = []
  ;['meter', 'water', 'air'].forEach(type => {
    const total = required(data, type + 'Count'); const down = required(data, type + 'OfflineCount')
    if (!Number.isInteger(total) || !Number.isInteger(down) || down < 0 || total < down) throw new Error('设备总量和离线量不一致')
    const abnormal = numberOrNull(data.meterAbnormalOfflineCount)
    categories.push({ key: type, label: { meter: '电表', water: '水表', air: '空调内机' }[type], total, offline: down, abnormal: type === 'meter' && Number.isInteger(abnormal) && abnormal >= 0 && abnormal <= down ? abnormal : null })
    devices += total; offline += down
  })
  return { devices, offline, online: devices - offline, categories }
}
export function allowedProjects (projects, managers) {
  if (!Array.isArray(managers)) throw new Error('管理方权限列表格式异常')
  const ids = new Set(managers.map(m => String(m.managerId)))
  return projects.filter(p => ids.has(String(p.id)))
}
function pvData (response) {
  if (!response) throw new Error('光伏服务数据缺失')
  let data = response
  while (data && !Array.isArray(data) && typeof data === 'object') {
    if (data.success === false || (data.code != null && !['0', '200'].includes(String(data.code)))) throw new Error('光伏服务返回异常')
    if (data.data == null) break
    data = data.data
  }
  return data
}
export function normalizeGeneration (response, range) {
  const data = pvData(response)
  let records = Array.isArray(data) ? data : data && (data.records || data.list)
  if (!Array.isArray(records) || !records.length) throw new Error('光伏统计暂无记录')
  // 月记录为 YYYY-MM-DD，年记录为 YYYY-MM；排除未来日期的占位记录。
  if (range) records = records.filter(r => !r.dateStr || (String(r.dateStr).slice(0, 7) >= range.startDate.slice(0, 7) && String(r.dateStr) <= range.endDate))
  if (!records.length) throw new Error('所选周期暂无光伏记录')
  const sum = (key, optional = false) => {
    if (optional && records.some(r => numberOrNull(r[key]) === null)) return null
    return records.reduce((total, r) => {
      const unit = String(r.energyStr || 'MWh').toLowerCase()
      const factor = { mwh: 1000, kwh: 1, wh: 0.001 }[unit]
      if (!factor) throw new Error('不支持的光伏电量单位')
      // 与 photovoltaic 页面一致：锦浪缺省精度 0.001，缺省单位 MWh。
      const pec = r.energyPec == null || r.energyPec === '' ? 0.001 : required(r, 'energyPec')
      const amount = required(r, key)
      if (pec <= 0 || amount < 0) throw new Error('光伏电量或精度异常')
      return total + amount * pec * factor
    }, 0)
  }
  const pv = sum('energy'); const self = sum('oneSelf', true); const exported = sum('gridSellEnergy', true)
  const validSplit = self !== null && exported !== null && Math.abs(pv - self - exported) < Math.max(0.1, pv * 0.001)
  return { pv, self: validSplit ? self : null, exported: validSplit ? exported : null }
}
// 本地复制锦浪运行记录换算；不引用旧页面的适配器。
export function normalizeSolarStatistics (response, range) {
  const value = normalizeGeneration(response, range)
  const data = pvData(response)
  const records = (Array.isArray(data) ? data : data.records || data.list).filter(r => !range || !r.dateStr || (String(r.dateStr).slice(0, 7) >= range.startDate.slice(0, 7) && String(r.dateStr) <= range.endDate))
  const amount = (r, key) => {
    const raw = numberOrNull(r[key])
    const factor = { mwh: 1000, kwh: 1, wh: 0.001 }[String(r.energyStr || 'MWh').toLowerCase()]
    return raw === null ? null : raw * (r.energyPec == null || r.energyPec === '' ? 0.001 : Number(r.energyPec)) * factor
  }
  return { ...value, gridImport: records.reduce((sum, r) => sum + (amount(r, 'gridPurchasedEnergy') || 0), 0), series: records.filter(r => r.dateStr).map(r => ({ date: String(r.dateStr), generation: amount(r, 'energy'), self: amount(r, 'oneSelf'), exported: amount(r, 'gridSellEnergy') })).sort((a, b) => a.date.localeCompare(b.date)) }
}

// 配置与原 energy-screen 一致；年收益依据上月表计电费，不把发电量乘单价冒充该接口金额。
export function solarReturnAnalysis (fee, generation, month) {
  const investment = 760000
  const annual = fee === null ? null : fee * 12
  return { month, fee, generation, investment, capacity: 330.86, price: 0.672, annual, returnRate: annual === null ? null : annual / investment * 100, payback: annual > 0 ? investment / annual : null }
}

export function normalizePvPower (response) {
  const payload = pvData(response)
  const data = payload.stationDetail || payload
  const power = required(data, 'power')
  if (power < 0) throw new Error('光伏功率异常')
  const unit = String(data.powerStr || 'kW').toLowerCase()
  const factor = { w: 0.000001, kw: 0.001, mw: 1 }[unit]
  if (!factor) throw new Error('不支持的光伏功率单位')
  // 与 energy-screen/api/energyScreenApi.js 相同：电站累计环保字段 kg -> t。
  // 累计值不随本页月/年筛选缩放；字段缺失保留 null，不用发电量猜测系数。
  const carbonKg = numberOrNull(data.powerStationAvoidedCo2)
  const validCarbon = carbonKg !== null && carbonKg >= 0 ? carbonKg : null
  // 复制原光伏页的上报时间优先级，区别于本屏请求完成时间。
  const reportedAt = [data.dataTimestamp, data.updateDate].map(numberOrNull).find(value => value > 0)
  const reportedTime = reportedAt ? new Date(reportedAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }) : null
  return { pvReportedTime: reportedTime, pvPower: power * factor, carbonKg: validCarbon, carbon: validCarbon === null ? null : validCarbon / 1000 }
}

/** 从原光伏页复制五分钟归桶算法；每设备同桶取最大值，再跨设备求和。 */
export function mapOverviewLoadSlots (
  records,
  { stepMinutes = 5, completedBefore = null } = {}
) {
  const slotCount = (24 * 60) / stepMinutes
  const slots = new Array(slotCount).fill(null)
  const slotDeviceMax = Array.from({ length: slotCount }, () => new Map())
  if (!Array.isArray(records) || records.length === 0) return slots

  const bucketMs = stepMinutes * 60 * 1000
  const completedBeforeMs = completedBefore == null
    ? null
    : new Date(completedBefore).getTime()

  records.forEach((record, recordIndex) => {
    if (!record || !record.ts) return
    const timePart = String(record.ts).split(' ')[1]
    if (!timePart) return
    const parts = timePart.split(':')
    const h = Number(parts[0])
    const m = Number(parts[1])
    const s = Number(parts[2] || 0)
    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return

    // 按 [00:00:00, 00:04:59] 这样的自然时间区间向下分桶
    const totalSeconds = h * 3600 + m * 60 + s
    const slotIdx = Math.floor(totalSeconds / (stepMinutes * 60))
    if (slotIdx < 0 || slotIdx >= slotCount) return

    if (Number.isFinite(completedBeforeMs)) {
      const timestamp = new Date(String(record.ts).replace(' ', 'T')).getTime()
      if (!Number.isFinite(timestamp)) return
      const bucketStart = Math.floor(timestamp / bucketMs) * bucketMs
      if (bucketStart + bucketMs > completedBeforeMs) return
    }

    const p = numberOrNull(record.p)
    if (p !== null && Number.isFinite(p) && p >= 0) {
      // 缺少 clientId 时无法判断设备覆盖，只保留为独立记录参与功率汇总
      const deviceKey = record.clientId == null || record.clientId === ''
        ? `__record_${recordIndex}`
        : String(record.clientId)
      const currentMax = slotDeviceMax[slotIdx].get(deviceKey)
      if (currentMax == null || p > currentMax) {
        slotDeviceMax[slotIdx].set(deviceKey, p)
      }
    }
  })

  for (let i = 0; i < slotCount; i++) {
    const deviceMax = slotDeviceMax[i]
    if (deviceMax.size === 0) continue
    slots[i] = Array.from(deviceMax.values()).reduce((sum, p) => sum + p, 0)
  }

  return slots
}

export function normalizeSolarDay (response, loadRecords, date, now = new Date()) {
  const data = pvData(response)
  const records = Array.isArray(data) ? data : data && (data.records || data.list)
  if (!Array.isArray(records)) throw new Error('日运行曲线格式异常')
  const loads = mapOverviewLoadSlots((loadRecords || []).filter(r => String(r.ts || '').slice(0, 10) === date), { completedBefore: new Date(now.getTime() - 120000) })
  const slots = Array.from({ length: 288 }, (_, i) => ({ label: String(Math.floor(i / 12)).padStart(2, '0') + ':' + String(i % 12 * 5).padStart(2, '0'), pv: null, load: loads[i], grid: null }))
  records.forEach(r => {
    const label = String(r.timeStr || '').slice(0, 5)
    const slot = slots.find(v => v.label === label)
    if (!slot || new Date(date + 'T' + label + ':00').getTime() > now.getTime()) return
    const value = numberOrNull(r.power); const factor = numberOrNull(r.powerPec)
    if (value !== null && value >= 0) slot.pv = value * (factor === null ? 0.001 : factor)
  })
  // Copy the original photovoltaic page's merge rule: a valid EMS load
  // still gives a grid import curve before sunrise when PV has no power record.
  const today = moment(now).format('YYYY-MM-DD')
  const currentIndex = Math.floor((now.getHours() * 60 + now.getMinutes()) / 5)
  slots.forEach((slot, index) => {
    if (slot.load !== null) slot.grid = (slot.pv || 0) - slot.load
    if (slot.pv === 0) slot.pv = null
    if (date === today && index === currentIndex) {
      slot.pv = slot.load = slot.grid = null
      slot.isCollecting = true
    } else if (date === today && index === currentIndex - 1 && slot.load === null) {
      slot.isSettling = true
    }
  })
  return { date, slots }
}

/** transport 可注入，仅供单元测试；生产默认共享鉴权请求器，不复制 token。 */
export function createOverviewApi (transport = request, externalFetch = (...args) => fetch(...args)) {
  const post = (url, data) => transport({ url, method: 'post', data, headers: HEADERS, skipErrorMessage: true })
  let solarQueue = Promise.resolve(); let lastSolarCall = 0
  const cache = new Map()
  const pvPost = async (path, data) => {
    const body = { stationId: '1299184320439459357', timeZone: 8, ...data }
    const key = path + JSON.stringify(body)
    const cached = cache.get(key)
    if (cached && Date.now() - cached.time < 120000) return cached.promise
    const promise = solarQueue.then(async () => {
      const wait = Math.max(0, 2000 - (Date.now() - lastSolarCall))
      if (wait) await new Promise(resolve => setTimeout(resolve, wait))
      lastSolarCall = Date.now()
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timer = controller ? setTimeout(() => controller.abort(), 10000) : null
      try {
        const base = process.env.NODE_ENV === 'development' ? '/api/asset-command-pv/' : 'https://grid.geekpartytech.com/api/station/'
        const response = await externalFetch(base + path, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller ? controller.signal : undefined })
        if (!response.ok) throw new Error('光伏服务请求失败：HTTP ' + response.status)
        const result = await response.json()
        pvData(result)
        return result
      } finally { clearTimeout(timer) }
    })
    cache.set(key, { time: Date.now(), promise })
    solarQueue = promise.catch(() => { cache.delete(key) })
    return promise
  }
  const settled = async task => {
    try { return { state: 'ready', value: await task() } } catch (e) { return { state: 'error', message: e.message || '请求失败' } }
  }

  // 南都当日曲线专用：复制 api/ems.js 的只读登录及功率参数请求，缓存独立。
  const EMS_BASE = 'https://cp-ems.szcloudpulse.com:86/prod-api'
  const LOGIN_CREDENTIALS = { username: 'jipai', password: '123456' }
  let emsToken = null; let emsExpiry = 0
  async function emsJson (url, options) {
    const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 12000)
    try {
      const res = await externalFetch(url, { ...options, signal: controller.signal })
      if (!res.ok) throw new Error('南都负载服务暂不可用')
      const body = await res.json()
      if (Number(body.code) !== 200) throw new Error('南都负载统计返回异常')
      return body.data
    } finally { clearTimeout(timer) }
  }
  async function dayLoad (date) {
    if (!emsToken || Date.now() > emsExpiry) {
      const login = await emsJson(EMS_BASE + '/mockLogin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(LOGIN_CREDENTIALS) })
      if (!login || !login.token) throw new Error('南都负载授权暂不可用')
      emsToken = login.token; emsExpiry = Date.now() + 25 * 60 * 1000
    }
    const params = new URLSearchParams({ areaId: '2062004309533409281', startTime: date + ' 00:00:00', endTime: date + ' 23:59:59' })
    const records = await emsJson(EMS_BASE + '/dataQuery/powerParam?' + params, { method: 'POST', headers: { Authorization: 'Bearer ' + emsToken } })
    if (!Array.isArray(records)) throw new Error('负载曲线格式异常')
    return records
  }

  return {
    async managers (projects) { return allowedProjects(projects, success(await post(CONTENT + '/manager/getManagerListByUser'))) },
    async project (project, range, now = new Date()) {
      const dto = managerDto(project.id, range)
      const today = moment(now).format('YYYY-MM-DD')
      // 设备在线是当前快照，切换财务周期不能将设备数量缩放或查询成历史状态。
      const deviceDto = managerDto(project.id, { startDate: today, endDate: today })
      const billDto = { managerId: Number(project.id), tenantId: '', tenantStatus: '0', tenantType: 0, startDate: range.startDate, endDate: range.endDate }
      // The unfiltered response serves both tenant billing and existing all-mode
      // water/electricity statistics. Prepaid collection has its own request.
      const billResponse = post('/api/ebc-settle/capitalManage/tenantSettleStatis', billDto).then(success)
      const [energy, bills, devices, water, prepaidBills, tenantBills] = await Promise.all([
        settled(async () => normalizeEnergy(success(await post(CONTENT + '/energyScreen/getShareEnergy', dto)))),
        settled(async () => normalizeBills(await billResponse)),
        settled(async () => normalizeDevices(success(await post(CONTENT + '/energyScreen/getDeviceTopStatistics', deviceDto)))),
        settled(async () => {
          const values = await Promise.all([0, 1].map(tenantType => post(CONTENT + '/useReport/queryTenantWaterUseStatis', { managerId: Number(project.id), startDate: range.startDate, endDate: range.endDate, dataType: 2, useType: 0, tenantType })))
          return normalizeWater(success(values[0]), success(values[1]))
        }),
        settled(async () => normalizeBills(success(await post('/api/ebc-settle/capitalManage/tenantSettleStatis', { ...billDto, payType: 0 })))),
        settled(async () => ({ tenantFees: round(required(await billResponse, 'billTotalAmt')) }))
      ])
      return { energy, bills, devices, water, prepaidBills, tenantBills }
    },
    async solar (range) {
      const today = moment().format('YYYY-MM-DD')
      const dailyTask = (async () => {
        const [pv, load, month] = await Promise.all([
          settled(() => pvPost('day', { time: today })), settled(() => dayLoad(today)),
          settled(() => pvPost('month', { month: today.slice(0, 7) }))
        ])
        const curve = pv.state === 'ready' ? await settled(() => normalizeSolarDay(pv.value, load.state === 'ready' ? load.value : [], today)) : pv
        const summary = month.state === 'ready' ? await settled(() => { const data = pvData(month.value); const rows = Array.isArray(data) ? data : data.records || data.list || []; return normalizeGeneration(rows.filter(row => String(row.dateStr).slice(0, 10) === today)) }) : month
        return { date: today, curve, summary, loadState: load.state }
      })()
      const year = range.period === 'year'
      const [generation, power] = await Promise.all([
        settled(async () => normalizeSolarStatistics(await pvPost(year ? 'year' : 'month', year ? { year: range.startDate.slice(0, 4) } : { month: range.startDate.slice(0, 7) }), range)),
        settled(async () => normalizePvPower(await pvPost('detail')))
      ])
      const previous = overviewRange('last')
      const returns = await settled(async () => {
        const [bill, generated] = await Promise.all([
          post('/api/ebc-settle/settle/settleTenantStatistics', { managerId: 1000107, rid: '', tenantType: 3, payType: null, payStatus: '', withdrawStatus: '', settleWay: '0,1,2,3,4,5,10', status: '', startDate: previous.startDate, endDate: previous.endDate }),
          pvPost('month', { month: previous.startDate.slice(0, 7) })
        ])
        return solarReturnAnalysis(required(success(bill), '表计电费'), normalizeGeneration(generated, previous).pv, previous.label)
      })
      return { generation, power, returns, daily: await dailyTask }
    }
  }
}

/** 并发最多两个项目（每项目六个只读接口），期间切换可停止继续排队。 */
export async function loadOverview (api, projects, range, isCurrent = () => true) {
  const records = {}; let cursor = 0
  const solarTask = projects.some(p => String(p.id) === '1000107') ? api.solar(range) : Promise.resolve(null)
  const worker = async () => {
    while (cursor < projects.length && isCurrent()) {
      const p = projects[cursor++]
      records[p.id] = await api.project(p, range)
    }
  }
  await Promise.all([worker(), worker()])
  return { records, solar: await solarTask, fetchedAt: new Date() }
}

export function summarizeOverview (projects, state, period = 'month') {
  const m = aggregatePortfolio(projects, period)
  const rows = projects.map(p => (state.records || {})[p.id])
  const coverage = {}
  const sum = (group, fields, prefix = '') => {
    const ready = rows.filter(r => r && r[group] && r[group].state === 'ready')
    coverage[group] = { ready: ready.length, total: rows.length }
    fields.forEach(key => { m[prefix ? prefix + key[0].toUpperCase() + key.slice(1) : key] = rows.length && ready.length === rows.length ? ready.reduce((v, r) => v + r[group].value[key], 0) : null })
  }
  sum('energy', ['kwh', 'tenant', 'common', 'tenantGeneral', 'tenantAir', 'commonGeneral', 'commonAir'])
  sum('bills', ['fees', 'received', 'outstanding', 'electricityFees', 'waterFees', 'electricityReceived', 'waterReceived'])
  sum('tenantBills', ['tenantFees'])
  sum('prepaidBills', ['fees', 'received', 'outstanding', 'electricityFees', 'waterFees', 'electricityReceived', 'waterReceived'], 'prepaid')
  sum('water', ['water', 'tenantWater', 'commonWater'])
  sum('devices', ['devices', 'offline', 'online'])
  // 水电金额先按项目保留分，再汇总；保持独立真实口径。
  ;['fees', 'received', 'outstanding', 'tenantFees', 'prepaidFees', 'prepaidReceived', 'prepaidOutstanding'].forEach(k => { if (m[k] !== null) m[k] = round(m[k]) })
  m.parkingPreviewRows = overviewParkingPreview(projects, period)
  m.parkingPreview = m.parkingPreviewRows.reduce((sum, row) => sum + row.total, 0)
  m.parkingIncome = m.parkingPreview
  // 账单金额 = 租户应收 + 真实停车收入 + 充电模拟收入，在弹窗中分别披露。
  m.chargingPreviewRows = overviewChargingPreview(projects, period)
  m.chargeIncome = m.chargingPreviewRows.reduce((total, row) => total + row.total, 0)
  m.revenue = m.tenantFees === null ? null : round(m.tenantFees + m.parkingPreview + m.chargeIncome)
  m.prepaidCollection = divide(m.prepaidReceived, m.prepaidFees) === null ? null : divide(m.prepaidReceived, m.prepaidFees) * 100
  m.prepaidOutstandingRate = divide(m.prepaidOutstanding, m.prepaidFees) === null ? null : divide(m.prepaidOutstanding, m.prepaidFees) * 100
  m.outstandingRate = divide(m.outstanding, m.fees) === null ? null : divide(m.outstanding, m.fees) * 100
  m.collection = divide(m.received, m.fees) === null ? null : divide(m.received, m.fees) * 100
  m.onlineRate = divide(m.online, m.devices) === null ? null : divide(m.online, m.devices) * 100
  m.hasPv = projects.some(p => String(p.id) === '1000107')
  const solar = state.solar || {}
  m.pv = m.hasPv && solar.generation && solar.generation.state === 'ready' ? solar.generation.value.pv : null
  m.pvReportedTime = m.hasPv && solar.power && solar.power.state === 'ready' ? solar.power.value.pvReportedTime || null : null
  m.solarDaily = m.hasPv ? solar.daily || null : null
  m.pvPower = m.hasPv && solar.power && solar.power.state === 'ready' ? solar.power.value.pvPower : null
  m.carbon = m.hasPv && solar.power && solar.power.state === 'ready' ? numberOrNull(solar.power.value.carbon) : null
  m.carbonKg = m.hasPv && solar.power && solar.power.state === 'ready' ? numberOrNull(solar.power.value.carbonKg) : null
  // 自用取电站明确返回的 oneSelf，而非用 generation - 0 占位上网量来臆算。
  const generation = m.hasPv && solar.generation && solar.generation.state === 'ready' ? solar.generation.value : {}
  m.self = numberOrNull(generation.self); m.exported = numberOrNull(generation.exported)
  m.selfRate = divide(m.self, m.pv) === null ? null : divide(m.self, m.pv) * 100
  // 复制能源大屏的购电计算：总用电 - 光伏发电 + 电站购电修正（缺省 0）。
  // 是供需平衡计算值，不表述为购电总表实测。电站请求失败时保留缺失。
  m.gridCorrection = m.hasPv ? numberOrNull(generation.gridImport) || 0 : 0
  m.grid = m.kwh === null || (m.hasPv && m.pv === null) ? null : Math.max(0, m.kwh - (m.hasPv ? m.pv : 0) + m.gridCorrection)
  m.solarSeries = m.hasPv ? generation.series || [] : []
  m.solarReturn = m.hasPv && solar.returns && solar.returns.state === 'ready' ? solar.returns.value : null
  // 沿用能源大屏：发电量 / 同范围、同周期用电；不是光伏自用率。
  m.greenRate = divide(m.pv, m.kwh) === null ? null : divide(m.pv, m.kwh) * 100
  m.risk = overviewAlerts(projects).length
  m.open = m.risk
  m.coverage = coverage
  return m
}

export function overviewKpis (m) {
  const metric = (label, value, unit, note, tone = '') => ({ label, value, unit, note, tone })
  const f = formatMetric
  return [
    metric('账单金额', f(divide(m.revenue, 10000), 2), '万元', '租户应收 + 停车收入 + 充电模拟'),
    metric('本期综合用电', f(divide(m.kwh, 10000), 2), '万 kWh', '租户与公区合计'),
    metric('CO₂减排', f(m.carbon, 2), 't', m.hasPv ? '南都光伏 · 电站累计' : '当前范围未接入光伏', 'mint'),
    metric('光伏发电占比', f(m.greenRate, 2), '%', m.hasPv ? '光伏发电 / 当前范围用电' : '当前范围未接入光伏', 'mint'),
    metric('设备在线率', f(m.onlineRate, 1), '%', f(m.online) + ' / ' + f(m.devices) + ' 台 · 表计及空调', 'mint'),
    metric('待处理风险', f(m.risk), '项', `${m.count} 个项目集中管控`, 'gold')
  ]
}
